import { useDebugValue, useMemo, useReducer } from 'react';
import type { Draft } from 'immer';
import { produce } from 'immer';

/** @public can used for typing your actions */
export type PayloadAction<P> = {
    type: string;
    payload: P;
};

export type PayloadActionDispatch<P = void> = void extends P ? () => void : (payload: P) => void;

export type ReducerWithoutPayload<S> = (state: S) => S;

export type PayloadActionReducer<S, P = void> = (
    state: Draft<S>,
    action: PayloadAction<P>,
) => void | S | Draft<S>;

export type ReducerMap<State> = {
    [actionType: string]: PayloadActionReducer<State, any>;
};

export type DispatcherMap<Reducers extends ReducerMap<any>> = {
    [T in keyof Reducers]: Reducers[T] extends ReducerWithoutPayload<any>
        ? PayloadActionDispatch<void>
        : Reducers[T] extends PayloadActionReducer<any, infer P>
          ? PayloadActionDispatch<P>
          : never;
};

export interface UseLocalSliceOptions<State, Reducers extends ReducerMap<State>> {
    initialState: State;
    reducers: Reducers;
    name?: string;
}

/**
 * Creates a local state slice with a reducer-like API, using immer for immutable updates.
 * This hook acts as a direct replacement for useReducer, providing isolated state for each call.
 *
 * @param options - The configuration options for the local slice.
 * @param options.initialState - The initial state of the slice.
 * @param options.reducers - An object containing reducer functions for state updates.
 * @param [options.name='unnamed'] - An optional name for the slice, useful for debugging.
 *
 * @returns A tuple containing the current state and an object of dispatch functions.
 *
 * @example
 * import React from 'react';
 * import { useLocalSlice } from '@luna/state';
 *
 * interface CounterState {
 *   count: number;
 * }
 *
 * const useCounter = () => {
 *   return useLocalSlice({
 *     initialState: { count: 0 },
 *     reducers: {
 *       increment: (state) => { state.count += 1 },
 *       decrement: (state) => { state.count -= 1 },
 *       incrementBy: (state, action: { payload: number }) => {
 *         state.count += action.payload;
 *       },
 *     },
 *     name: 'counter',
 *   });
 * };
 *
 * const Counter = () => {
 *   const [state, actions] = useCounter();
 *
 *   return (
 *     <div>
 *       <p>Count: {state.count}</p>
 *       <button onClick={actions.increment}>Increment</button>
 *       <button onClick={actions.decrement}>Decrement</button>
 *       <button onClick={() => actions.incrementBy(5)}>Increment by 5</button>
 *     </div>
 *   );
 * };
 *
 */
export function useLocalSlice<State, Reducers extends ReducerMap<State>>({
    initialState,
    reducers,
    name = 'unnamed',
}: UseLocalSliceOptions<State, Reducers>): [State, DispatcherMap<Reducers>] {
    useDebugValue(name);

    const reducer = (baseState: State, action: PayloadAction<any>): State =>
        produce(
            baseState,
            (draftState) => reducers[action.type](draftState, action) as Draft<State>,
        );

    const [state, dispatch] = useReducer(reducer, initialState);

    const actionTypes = Object.keys(reducers);

    const dispatchAction = useMemo(() => {
        const map: {
            [actionType: string]: PayloadActionDispatch<Record<string, PayloadActionDispatch>>;
        } = {};

        actionTypes.forEach((type) => {
            map[type] = (payload: any) => dispatch({ type, payload });
        });

        return map as DispatcherMap<Reducers>;
    }, [dispatch, JSON.stringify(actionTypes)]);

    return [state, dispatchAction];
}
